// M12 Medien – Bibliothek (Upload per Drag-and-drop, Fortschritt je Datei, Suche, Filter, Speicherbalken)
// und M13 Medium – Detail als Dialog (Vorschau, Metadaten, Versionen, Verwendung, Löschen).
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError, api, uploadFile } from '../api/client';
import { can, qk, useMe, useMedia, useMediaOne, useUsage } from '../api/queries';
import type { Media } from '../api/types';
import { Icon } from '../components/Icon';
import { MediaView } from '../components/Preview';
import { errorText, useToast } from '../components/toast';
import { Dialog, Empty, Field, formatBytes, formatDateTime, PageHead, Spinner } from '../components/ui';
import { localeOf } from '../i18n';

interface UploadRow { key: number; name: string; sent: number; total: number; error?: string; done?: boolean }
let seq = 0;

/** Upload mehrerer Dateien mit Fortschritt; gemeinsam für Bibliothek und Playlist-Editor. */
export function useUploader() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<UploadRow[]>([]);
  const { t } = useTranslation();
  const upload = async (files: File[], opts: { assetId?: string } = {}) => {
    const created: string[] = [];
    for (const f of files) {
      const key = ++seq;
      setRows((r) => [...r, { key, name: f.name, sent: 0, total: f.size }]);
      try {
        const res = await uploadFile(f, (sent, total) => setRows((r) => r.map((x) => (x.key === key ? { ...x, sent, total } : x))), opts);
        created.push(res.assetId);
        setRows((r) => r.map((x) => (x.key === key ? { ...x, done: true } : x)));
      } catch (e) {
        setRows((r) => r.map((x) => (x.key === key ? { ...x, error: errorText(e, t) } : x)));
      }
    }
    await qc.invalidateQueries({ queryKey: qk.media });
    await qc.invalidateQueries({ queryKey: qk.usage });
    setTimeout(() => setRows((r) => r.filter((x) => !x.done)), 2500);
    return created;
  };
  return { rows, upload };
}

export function UploadList({ rows }: { rows: UploadRow[] }) {
  const { t } = useTranslation();
  if (!rows.length) return null;
  return (
    <div className="uploads" aria-live="polite">
      {rows.map((r) => (
        <div key={r.key} className="u">
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
          {r.error ? <span className="field-error" style={{ gridColumn: 'span 2' }}>{r.error}</span> : <>
            <div className="bar"><div style={{ width: `${r.total ? (r.sent / r.total) * 100 : 0}%` }} /></div>
            <span className="small muted">{r.done ? t('media.uploaded') : `${Math.round(r.total ? (r.sent / r.total) * 100 : 0)} %`}</span>
          </>}
        </div>
      ))}
    </div>
  );
}

export function DropZone({ onFiles, compact }: { onFiles: (f: File[]) => void; compact?: boolean }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div className={`drop${over ? ' over' : ''}`} role="button" tabIndex={0} aria-label={t('media.drop')}
      style={compact ? { padding: 16 } : undefined}
      onClick={() => input.current?.click()} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles([...e.dataTransfer.files]); }}>
      <div style={{ color: 'var(--accent)' }}><Icon name="upload" /></div>
      <div style={{ fontWeight: 600, marginTop: 6 }}>{t('media.drop')}</div>
      <div className="muted small">JPG, PNG, WebP, GIF, MP4, WebM</div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" multiple hidden data-testid="file-input"
        onChange={(e) => { onFiles([...(e.target.files ?? [])]); e.target.value = ''; }} />
    </div>
  );
}

export function MediaPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: media, isLoading } = useMedia();
  const usage = useUsage();
  const { rows, upload } = useUploader();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const list = useMemo(() => (media ?? []).filter((m) => (!q || m.name.toLowerCase().includes(q.toLowerCase()) || m.tags.some((x) => x.includes(q)))
    && (!kind || m.kind === kind)), [media, q, kind]);
  const canUpload = can(me, 'media.upload');
  const u = usage.data;

  return (
    <>
      <PageHead title={t('nav.media')} sub={t('media.sub')} />
      {canUpload && <DropZone onFiles={(f) => void upload(f)} />}
      <UploadList rows={rows} />
      {u && (
        <div className="row small muted" style={{ marginTop: 14 }}>
          <span>{t('dashboard.storage')}</span>
          <div className="bar grow" style={{ maxWidth: 260 }}><div style={{ width: `${Math.min(100, (u.storageUsedBytes / Math.max(1, u.storageQuotaBytes)) * 100)}%` }} /></div>
          <span>{t('dashboard.storageOf', { used: formatBytes(u.storageUsedBytes, locale), quota: formatBytes(u.storageQuotaBytes, locale) })}</span>
        </div>
      )}
      <div className="toolbar" style={{ marginTop: 18, marginBottom: 0 }}>
        <input type="search" placeholder={t('common.search')} aria-label={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        <select aria-label={t('media.kind')} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">{t('media.allKinds')}</option><option value="image">{t('media.image')}</option><option value="video">{t('media.video')}</option>
        </select>
      </div>
      {isLoading ? <Spinner /> : !media?.length ? (
        <div className="card" style={{ marginTop: 18 }}><Empty icon="image" title={t('media.empty')} text={t('media.emptyText')} /></div>
      ) : (
        <div className="media-grid">
          {list.map((m) => (
            <figure key={m.id} tabIndex={0} role="button" aria-label={m.name} onClick={() => setOpen(m.id)} onKeyDown={(e) => e.key === 'Enter' && setOpen(m.id)} style={{ cursor: 'pointer' }}>
              <MediaView media={m} />
              <span className="badge">{m.kind === 'image' ? t('media.image') : t('media.video')}</span>
              <figcaption>
                <span className="n" title={m.name}>{m.name}</span>
                <span className="row" style={{ gap: 6 }}>
                  {m.current && <span className={`pill plain ${m.current.status === 'ready' ? 'ok' : m.current.status}`}>{t(`media.status.${m.current.status}`)}</span>}
                  <span className="muted">{m.current?.width ? `${m.current.width}×${m.current.height}` : ''}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {open && <MediaDetailDialog id={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function MediaDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: m } = useMediaOne(id);
  const qc = useQueryClient();
  const toast = useToast();
  const { rows, upload } = useUploader();
  const [name, setName] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string[] | null>(null);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: qk.media }), qc.invalidateQueries({ queryKey: qk.mediaOne(id) })]);
  if (!m) return null;
  const v = m.current;

  const rename = async () => {
    if (name === null || name === m.name) return;
    try {
      await api.patch<Media>(`/media/${m.id}`, { name }, { ifMatch: m.rowVersion });
      await refresh();
      toast.show(t('common.saved'));
    } catch (e) {
      toast.error(e);
    }
  };
  const del = async () => {
    if (!window.confirm(t('media.confirmDelete', { name: m.name }))) return;
    try {
      await api.del(`/media/${m.id}`, { ifMatch: m.rowVersion });
      await qc.invalidateQueries({ queryKey: qk.media });
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setBlocked(e.problem.errors?.playlists ?? []);
      else toast.error(e);
    }
  };
  return (
    <Dialog wide title={m.name} onClose={onClose} actions={<>
      {can(me, 'media.delete') && <button className="btn ghost danger" onClick={del}><Icon name="trash" />{t('common.delete')}</button>}
      <span className="grow" />
      <button className="btn" onClick={onClose}>{t('common.close')}</button>
    </>}>
      <div className="grid-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <div className="tv" style={{ aspectRatio: '16/10' }}><MediaView media={m} controls /></div>
        <div className="col">
          {can(me, 'media.upload') ? (
            <Field label={t('common.name')}><input value={name ?? m.name} onChange={(e) => setName(e.target.value)} onBlur={rename} /></Field>
          ) : null}
          {v && <dl className="props small">
            <dt>{t('media.statusLabel')}</dt><dd>{t(`media.status.${v.status}`)}</dd>
            <dt>{t('media.type')}</dt><dd>{v.mimeType}</dd>
            <dt>{t('media.resolution')}</dt><dd>{v.width ? `${v.width} × ${v.height}` : '–'}</dd>
            {v.videoCodec && <><dt>{t('media.codec')}</dt><dd>{[v.videoCodec, v.videoProfile, v.audioCodec].filter(Boolean).join(' · ')}</dd></>}
            {v.durationMs ? <><dt>{t('media.length')}</dt><dd>{(v.durationMs / 1000).toFixed(1)} s</dd></> : null}
            <dt>{t('media.size')}</dt><dd>{formatBytes(v.sizeBytes, locale)}</dd>
            <dt>SHA-256</dt><dd className="mono" title={v.sha256}>{v.sha256.slice(0, 12)}…</dd>
            <dt>{t('media.uploadedAt')}</dt><dd>{formatDateTime(v.createdAt, locale)}</dd>
          </dl>}
          {v?.rejectReason && <div className="hint warn" role="alert">{v.rejectReason}</div>}
          {v?.warnings.map((w) => <div key={w} className="hint warn">{w}</div>)}
        </div>
      </div>
      <div>
        <b>{t('media.usage')}</b>{' '}
        {m.usedIn.length ? m.usedIn.map((u, i) => <span key={u.playlistId}>{i ? ', ' : ''}<Link to={`/playlists/${u.playlistId}`} onClick={onClose}>{u.name}</Link></span>)
          : <span className="muted">{t('media.unused')}</span>}
      </div>
      {blocked && <div className="hint warn" role="alert">{t('media.inUse', { list: blocked.join(', ') })}</div>}
      <div>
        <b>{t('media.versions')}</b>
        <ul className="list-plain small">
          {m.versions.map((x) => <li key={x.id}><span>v{x.versionNo}</span><span className="grow">{x.originalName}</span><span className="muted">{formatBytes(x.sizeBytes, locale)} · {formatDateTime(x.createdAt, locale)}</span></li>)}
        </ul>
      </div>
      {can(me, 'media.upload') && <>
        <DropZone compact onFiles={(f) => void upload(f.slice(0, 1), { assetId: m.id }).then(refresh)} />
        <UploadList rows={rows} />
      </>}
    </Dialog>
  );
}
